import { Store } from '@betgames/bg-state-manager';
import { IRGSFreePlayModel } from '@bg-shared/business/RGSFreePlay/interfaces';
import currency from 'currency.js';
import { IFreePlayAvailable, IFreePlayInfo } from '@bg-shared/business/RGSGameApi/interfaces';
import { GameId } from '@bg-shared/enums';
import { StoresEntity } from '@bg-shared/business/StoreFeature';

export class RGSFreePlay extends Store<IRGSFreePlayModel> {
    /**
     * Cached currency and stake values for the currently active free play.
     * These are preserved during multi-round games and when transitioning
     * to completion modal, preventing the next free play's values from
     * overwriting the current ones prematurely.
     */
    private activeFreePlayCurrency: string | null = null;

    private activeFreePlayStake: number | null = null;

    constructor(gameId: GameId) {
        super({
            key: `RGSFreePlay/${gameId}`,
            default: {
                info: null,
                available: [],
                currency: null,
                stake: null,
                active: false,
                isCompletionModal: false,
                isInitialModal: false,
                shouldShowModal: false,
                hasMoreFreePlays: false,
            },
        });
    }

    public init(info: IFreePlayInfo, available: IFreePlayAvailable[]) {
        if (!info || !available?.length) {
            return;
        }

        this.update((draft) => {
            draft.info = info;
            draft.available = available;
            draft.currency = available[0].currency;
            draft.stake = available[0].stake;
            draft.active = info.used > 0;

            // Cache values if resuming an active free play session
            if (draft.active) {
                this.activeFreePlayCurrency = available[0].currency;
                this.activeFreePlayStake = available[0].stake;
            }

            this.updateComputedStates(draft);
        });
    }

    public updateStore(
        isInSubsequentRound: boolean,
        isBonusGameFirstRound: boolean,
        latestWinAmount: number,
        info?: IFreePlayInfo,
        available?: IFreePlayAvailable[],
    ) {
        // Multi-round games don't include free play data in subsequent responses
        if ((isBonusGameFirstRound || isInSubsequentRound) && this.value.active) {
            this.update((draft) => {
                if (isBonusGameFirstRound && !isInSubsequentRound) {
                    draft.info = info;
                    draft.available = available ?? [];
                }
                if (draft.info) {
                    draft.info.won = currency(draft.info.won).add(latestWinAmount).value;
                }
                this.updateComputedStates(draft);
            });
            return;
        }

        // General update with free play data
        this.update((draft) => {
            draft.info = info;
            draft.available = available ?? [];

            const isActiveLastFreePlay = info?.isLast && draft.active;
            const hasCachedValues = this.activeFreePlayCurrency && this.activeFreePlayStake;

            // When showing completion modal, preserve the active free play's currency/stake
            // instead of using the next free play's values from available[0]
            if (isActiveLastFreePlay && hasCachedValues) {
                draft.currency = this.activeFreePlayCurrency;
                draft.stake = this.activeFreePlayStake;
            } else if (available?.length) {
                draft.currency = available[0].currency;
                draft.stake = available[0].stake;
            } else if (!info && !available?.length) {
                draft.currency = null;
                draft.stake = null;
            }

            this.updateComputedStates(draft);
        });
    }

    public handleModalDismiss() {
        this.update((draft) => {
            const wasCompletionModal = draft.isCompletionModal;

            if (wasCompletionModal) {
                // Completion modal dismissed - deactivate and prepare for next free play
                draft.active = false;
                draft.info = null;
                this.activeFreePlayCurrency = null;
                this.activeFreePlayStake = null;

                if (draft.available.length > 0) {
                    draft.currency = draft.available[0].currency;
                    draft.stake = draft.available[0].stake;
                    draft.shouldShowModal = true;
                }
            } else if (draft.isInitialModal) {
                // Initial modal dismissed - activate the free play and cache its values
                draft.active = true;
                this.activeFreePlayCurrency = draft.currency;
                this.activeFreePlayStake = draft.stake;
                draft.shouldShowModal = false;
            }

            this.updateComputedStates(draft);
        });
    }

    public markModalShown() {
        this.update((draft) => {
            draft.shouldShowModal = false;
        });
    }

    public requestModalShow() {
        this.update((draft) => {
            draft.shouldShowModal = true;
        });
    }

    private updateComputedStates(draft: IRGSFreePlayModel) {
        draft.isCompletionModal = !!(draft.info?.isLast && draft.active);
        draft.isInitialModal = !draft.active && draft.available.length > 0;
        draft.hasMoreFreePlays = draft.available.length > 0;
    }
}

export const rgsFreePlayEntity = new StoresEntity(RGSFreePlay);
